import {
  Alert,
  Button,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  TextField,
} from "@mui/material";
import { StatusBadge } from "./StatusBadge";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useState } from "react";
import { PageGroupEndPointLogic } from "../../../../../logic/AssetManagement/PageGroups/pageGroupEndPointLogic";
import store from "../../../../../store";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";

interface Props {
  filteredData: any;
  rowToDisable: any;
  setRowToDisable: any;
  open: boolean;
  onClose: () => void;
  handleConfirmDisable: any;
  toggleLoader: boolean;
  setToggleLoader: any;
  setSortedData: any;
  isFetchingPages: any;
  pages:any;
}

export const DeactivateConfirmationForPageGroups = ({
  filteredData,
  rowToDisable,
  setRowToDisable,
  open,
  onClose,
  toggleLoader,
  handleConfirmDisable,
  setToggleLoader,
  isFetchingPages,
  pages
}: Props) => {
  const token: any = useSelector(selectUserToken);
  const [searchParams] = useSearchParams();
  const [selectedGroups, setSelectedGroups] = useState({}) as any;
  const selectedSite = searchParams.get("selectedSite");

  const pageGroupOptions =
    filteredData.filter(
      (p: any) => Boolean(p?.group_name) && p.active === true,
    ) || [];

  const columns: GridColDef[] = [
    {
      field: "post_name",
      headerName: "Name",
      flex: 1,
      sortable: true, // enables sorting
    },
    {
      field: "post_status",
      headerName: "Status",
      flex: 1,
      sortable: true,
      renderCell: (params: any) => <StatusBadge status={params.value} />,
    },
    {
      field: "reassign",
      headerName: "New Page Group",
      flex: 1,
      sortable: false, // no sorting for buttons
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        const currentPageUid = params.row.shared_uid;
        return (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              marginTop: 20,
              marginBottom: 20,
            }}
          >
            <Autocomplete
              size="small"
              data-tracker="524"
              //filter out the page group that is to be deactivated
              options={pageGroupOptions.filter(
                (o: any) => o.shared_uid !== rowToDisable?.row?.shared_uid,
              )}
              onKeyDown={(e) => {
                if (
                  e.key === " " ||
                  e.key === "ArrowDown" ||
                  e.key === "ArrowUp"
                ) {
                  e.stopPropagation(); // Prevent space from closing dropdown
                }
              }}
              getOptionLabel={(option: any) => option?.group_name ?? ""}
              isOptionEqualToValue={(opt: any, val: any) =>
                (opt?.shared_uid ?? opt?.group_name) ===
                (val?.shared_uid ?? val?.group_name)
              }
              value={
                pageGroupOptions.find(
                  (o: any) => o.shared_uid === selectedGroups[currentPageUid],
                ) || null
              }
              onChange={(_e, value: any) => {
                setSelectedGroups((prev: any) => {
                  const updated = { ...prev };
                  if (value?.shared_uid) {
                    updated[currentPageUid] = value.shared_uid; // add/update
                  } else {
                    delete updated[currentPageUid]; // clear on deselect
                  }
                  return updated;
                });
              }}
              sx={{
                width: "100%",
                maxWidth: 260,
              }}
              renderInput={(paramsInput) => (
                <TextField
                  {...paramsInput}
                  color="secondary"
                  data-tracker="524"
                  placeholder="Select from list"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                />
              )}
            />
          </Box>
        );
      },
    },
  ];
  
  const rows: any =
    pages
      ?.filter(
        (page: any) =>page?.page_group_name === rowToDisable?.row?.group_name && page?.shared_uid !== null && page?.post_status !== "bin",
      )
      ?.map((page: any, index: number): any => ({
        id: page?.post_id || index,
        ...page,
      })) || []; // filtered the pages having status "bin"

  const reassignmentTasks = rows.filter(
    (page: any) => selectedGroups[page.shared_uid],
  ).length;

  const handleDeactivateWithReassignments = async () => {
    if (!selectedSite) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "No selected site found. Please select a site first.",
          type: "error",
        }),
      );
      return;
    }

    if (!rows || rows.length === 0) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Please reassign all pages before deactivation.",
          type: "warning",
        }),
      );
      return;
    }

    setToggleLoader(true);

    try {
      // Group pages by selected group
      const groupedPages: Record<string, string[]> = {};

      rows.forEach((page: any) => {
        const newGroupUid = selectedGroups[page.shared_uid];

        if (!newGroupUid) return;

        if (!groupedPages[newGroupUid]) {
          groupedPages[newGroupUid] = [];
        }

        groupedPages[newGroupUid].push(page.shared_uid);
      });

      const failures: string[] = [];

      // Step 2: Call API once per group
      for (const [groupUid, pagesSharedUids] of Object.entries(groupedPages)) {
        const payload = {
          page_group_id: groupUid,
          pages_shared_uids: pagesSharedUids,
        };

        try {
          const result = await PageGroupEndPointLogic.assignPageGroupToPages(
            { data: payload },
            selectedSite,
            token,
          );

          if (!result || result.status !== 200) {
            failures.push(...pagesSharedUids);
          }
        } catch (err) {
          failures.push(...pagesSharedUids);
        }
      }

      if (failures.length > 0) {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: `Reassignment failed for some pages.`,
            type: "error",
          }),
        );
        return;
      }

      // All successful
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "All pages reassigned successfully.",
          type: "success",
        }),
      );

      await handleConfirmDisable();
      onClose();
      setRowToDisable(null);
      setSelectedGroups({});
    } catch (err) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Unexpected error occurred during reassignment.",
          type: "error",
        }),
      );
    } finally {
      setToggleLoader(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setSelectedGroups({});
  };

  return (
    <>
      <Dialog open={open} sx={{ p: 3 }} fullWidth maxWidth="lg">
        <DialogTitle>
          Are you sure you want to deactivate this Page Group?
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            sx={{ width: "100%", mb: 2 }}
            icon={<InfoOutlinedIcon />}
          >
            <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 500 }}>
              You cannot deactivate a group that has pages assigned to it.
            </h2>
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 400 }}>
              Please reassign pages
            </h3>
          </Alert>
        </DialogContent>
        <DialogContent>
          <Box sx={{ height: 400, width: "100%" }}>
            {isFetchingPages ? (<SimpleBackdrop/>):
            (<DataGridPro
              rows={rows}
              columns={columns}
              disableColumnFilter
              disableColumnMenu
              disableDensitySelector
              disableRowSelectionOnClick
              sortingOrder={["asc", "desc"]}
              hideFooter
              sx={{
                border: "none", // remove outer border
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(224, 224, 224, 0.3)",
                  display: "flex",
                  alignItems: "center", // ✅ vertically center all cell content
                },
                //Remove blue focus/selection outlines completely
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                  {
                    outline: "none !important",
                  },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "inherit !important",
                },
                "& .MuiDataGrid-cell.Mui-selected": {
                  backgroundColor: "inherit !important",
                },
                "& .MuiAutocomplete-hasPopupIcon.css-1auh4w4-MuiAutocomplete-root .MuiOutlinedInput-root, .MuiAutocomplete-hasClearIcon.css-1auh4w4-MuiAutocomplete-root .MuiOutlinedInput-root":
                  {
                    marginTop: "20px",
                    marginBottom: "20px",
                  },
              }}
            />)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 3 }}>
          <Button
            onClick={handleCancel}
            color="secondary"
            disabled={toggleLoader}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateWithReassignments}
            data-tracker="525"
            color="secondary"
            variant="contained"
            sx={{ px: 3 }}
            disabled={toggleLoader || reassignmentTasks !== rows.length}
            endIcon={<SaveOutlinedIcon />}
            loading={toggleLoader}
          >
            Save Changes & Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
